import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { animate, m, useMotionValue } from "framer-motion";
import clsx from "clsx";
import { Trans } from "@ui/i18n/trans";
import { ChevronLeftIcon, ArrowBackIcon } from "@ui/icons/material/all-icons";
import { PlayerStoreContext } from "@common/player/player-context";
import {
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from "react-router-dom";
import { isMobileApp, isRenderedAsIOS } from "@common/core/platform-detection";
import { useSwipeable } from "react-swipeable";
import { DialogContext } from "@ui/overlays/dialog/dialog-context";
import { StackScreenContext } from "./stack-screen-context";
import {
  logStackNavigator,
  StackScreenEntry,
  StackScreenOptions,
  stackNavigatorState,
  useStackNavigatorStore,
} from "./stack-navigator-store";
import {
  AppEventId,
  useAppEventManager,
} from "@common/core/capacitor/app-event-manager";
import {
  STACK_SURFACE_Z_INDEX_BASE,
  STACK_SURFACE_Z_INDEX_STEP,
} from "@ui/overlays/overlay-z-index";

const IOS_SWIPE_ZONE = 50;
const IOS_STACKED_UNDERLAY_RATIO = 0.3;
const IOS_STACKED_UNDERLAY_OFFSET_VAR = "--be-stacked-underlay-offset-x";
const IOS_EASE_OUT = [0, 0, 0.58, 1] as [number, number, number, number];

const IOS_STACKED_TRANSITION = {
  type: "tween" as const,
  duration: 0.35,
  ease: IOS_EASE_OUT,
};

const ANDROID_DECELERATION_CURVE = [0, 0, 0.2, 1] as [
  number,
  number,
  number,
  number,
];
const ANDROID_ACCELERATION_CURVE = [0.4, 0, 1, 1] as [
  number,
  number,
  number,
  number,
];

const ANDROID_STACKED_TRANSITION = {
  type: "tween" as const,
  duration: 0.225,
  ease: ANDROID_DECELERATION_CURVE,
};

const IOS_CLOSE_SWIPE_TRANSITION = {
  duration: 0.22,
  ease: IOS_EASE_OUT,
};

const ANDROID_CLOSE_SWIPE_TRANSITION = {
  duration: 0.195,
  ease: ANDROID_ACCELERATION_CURVE,
};

const IOS_REBOUND_SWIPE_TRANSITION = {
  type: "spring" as const,
  stiffness: 380,
  damping: 34,
  mass: 0.88,
};

const ANDROID_REBOUND_SWIPE_TRANSITION = {
  type: "spring" as const,
  stiffness: 440,
  damping: 38,
  mass: 0.88,
};

type StackedUnderlayTarget = {
  kind: "stacked";
  element: HTMLElement;
};

type GenericUnderlayTarget = {
  kind: "generic";
  element: HTMLElement;
  initialStyles: {
    transform: string;
    willChange: string;
    backfaceVisibility: string;
    webkitBackfaceVisibility: string;
  };
};

type UnderlayTarget = StackedUnderlayTarget | GenericUnderlayTarget;

let stackedScrollLockCount = 0;
let stackedScrollY = 0;

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getIOSUnderlayOffset() {
  return Math.round(window.innerWidth * IOS_STACKED_UNDERLAY_RATIO);
}

function getIOSUnderlayProgress(deltaX: number) {
  return clampNumber(1 - deltaX / Math.max(window.innerWidth, 1), 0, 1);
}

function getViewportWidth() {
  return typeof window === "undefined" ? 0 : window.innerWidth;
}

function getViewportHeight() {
  return typeof window === "undefined" ? 0 : window.innerHeight;
}

function isSurfaceOffscreen(rect: DOMRect) {
  const viewportWidth = getViewportWidth();
  return rect.left > 8 || rect.right < viewportWidth - 8;
}

function setStackedUnderlayOffset(element: HTMLElement, offset: number) {
  element.style.setProperty(IOS_STACKED_UNDERLAY_OFFSET_VAR, `${offset}px`);
}

function clearStackedUnderlayOffset(element: HTMLElement) {
  setStackedUnderlayOffset(element, 0);
}

function createGenericUnderlayTarget(
  element: HTMLElement,
): GenericUnderlayTarget {
  return {
    kind: "generic",
    element,
    initialStyles: {
      transform: element.style.transform,
      willChange: element.style.willChange,
      backfaceVisibility: element.style.backfaceVisibility,
      webkitBackfaceVisibility: element.style.webkitBackfaceVisibility,
    },
  };
}

function resolveUnderlayTargets(
  previousSurface?: HTMLElement | null,
): UnderlayTarget[] {
  if (previousSurface) {
    return [{ kind: "stacked", element: previousSurface }];
  }

  const fallbackTargets = [
    ...Array.from(
      document.querySelectorAll<HTMLElement>(".app-ambient-content"),
    ),
    ...Array.from(
      document.querySelectorAll<HTMLElement>(".app-ambient-backdrop"),
    ),
  ];

  if (fallbackTargets.length) {
    return Array.from(new Set(fallbackTargets)).map((element) =>
      createGenericUnderlayTarget(element),
    );
  }

  const mainTarget = document.querySelector<HTMLElement>("main");
  return mainTarget ? [createGenericUnderlayTarget(mainTarget)] : [];
}

function applyUnderlayProgress(targets: UnderlayTarget[], progress: number) {
  const offset = -getIOSUnderlayOffset() * clampNumber(progress, 0, 1);

  targets.forEach((target) => {
    if (target.kind === "stacked") {
      const nextOffset = `${offset}px`;
      if (
        target.element.style.getPropertyValue(
          IOS_STACKED_UNDERLAY_OFFSET_VAR,
        ) !== nextOffset
      ) {
        setStackedUnderlayOffset(target.element, offset);
      }
      return;
    }

    const nextTransform =
      Math.abs(offset) > 0.5
        ? `translate3d(${offset}px, 0, 0)`
        : target.initialStyles.transform;

    if (target.element.style.transform !== nextTransform) {
      target.element.style.transform = nextTransform;
    }
  });
}

function prepareUnderlayTargets(targets: UnderlayTarget[]) {
  targets.forEach((target) => {
    if (target.kind === "stacked") {
      return;
    }

    target.element.style.willChange = target.initialStyles.willChange
      ? `${target.initialStyles.willChange}, transform`
      : "transform";
    target.element.style.backfaceVisibility = "hidden";
    target.element.style.webkitBackfaceVisibility = "hidden";
  });
}

function restoreUnderlayTargets(targets: UnderlayTarget[]) {
  targets.forEach((target) => {
    if (target.kind === "stacked") {
      clearStackedUnderlayOffset(target.element);
      return;
    }

    target.element.style.transform = target.initialStyles.transform;
    target.element.style.willChange = target.initialStyles.willChange;
    target.element.style.backfaceVisibility =
      target.initialStyles.backfaceVisibility;
    target.element.style.webkitBackfaceVisibility =
      target.initialStyles.webkitBackfaceVisibility;
  });
}

function lockStackedScroll(isNativeMobileApp: boolean) {
  const bodyEl = document.body;

  if (stackedScrollLockCount === 0) {
    const mainEl = document.querySelector("main");

    if (!isNativeMobileApp) {
      stackedScrollY = window.scrollY;
      bodyEl.style.position = "fixed";
      bodyEl.style.top = `-${stackedScrollY}px`;
      bodyEl.style.left = "0";
      bodyEl.style.right = "0";
    }

    mainEl?.classList.add("overflow-y-hidden");
    bodyEl.classList.add("overflow-y-hidden");
  }

  stackedScrollLockCount += 1;

  return () => {
    stackedScrollLockCount = Math.max(0, stackedScrollLockCount - 1);
    if (stackedScrollLockCount > 0) {
      return;
    }

    const mainEl = document.querySelector("main");

    mainEl?.classList.remove("overflow-y-hidden");
    bodyEl.classList.remove("overflow-y-hidden");

    if (!isNativeMobileApp) {
      bodyEl.style.position = "";
      bodyEl.style.top = "";
      bodyEl.style.left = "";
      bodyEl.style.right = "";
      window.scrollTo(0, stackedScrollY);
    }
  };
}

interface StackSurfaceProps {
  entry: StackScreenEntry;
  stackIndex: number;
  isTopScreen: boolean;
  canGoBack: boolean;
  previousSurfaceKey?: string | null;
  onCloseComplete: (key: string, value?: any) => void;
}

const registeredStackSurfaces = new Map<string, HTMLElement | null>();

export function getRegisteredStackSurface(key: string) {
  return registeredStackSurfaces.get(key) ?? null;
}

export function StackSurface({
  entry,
  stackIndex,
  isTopScreen,
  canGoBack,
  previousSurfaceKey,
  onCloseComplete,
}: StackSurfaceProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const surfaceHostRef = useRef<HTMLDivElement | null>(null);
  const surfaceMotionRef = useRef<HTMLDivElement | null>(null);
  const isNativeMobileApp = isMobileApp();
  const renderedAsIOS = isNativeMobileApp && isRenderedAsIOS();
  const shouldDeferBodyMount = renderedAsIOS;
  const transition = renderedAsIOS
    ? IOS_STACKED_TRANSITION
    : ANDROID_STACKED_TRANSITION;

  const x = useMotionValue(renderedAsIOS ? getViewportWidth() : 0);
  const y = useMotionValue(renderedAsIOS ? 0 : getViewportHeight());
  const opacity = useMotionValue(renderedAsIOS ? 1 : 0);
  const underlayProgress = useMotionValue(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isBodyReady, setIsBodyReady] = useState(!shouldDeferBodyMount);
  const isSwipingRef = useRef(false);
  const isInteractiveIOSRef = useRef(false);
  const isClosingRef = useRef(false);
  const prevDelta = useRef({ x: 0, y: 0 });
  const lastDirRef = useRef<"Right" | "Left" | "Down" | "Up" | null>(null);
  const closeValueRef = useRef<any>(undefined);
  const underlayTargetsRef = useRef<UnderlayTarget[]>([]);
  const underlayAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
  const settleAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
  const surfaceAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
  const opacityAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
  const enterAnimationFrameRef = useRef<number | null>(null);
  const ensureVisibleFrameRef = useRef<number | null>(null);
  const getUnderlaySurface = useCallback(
    () =>
      previousSurfaceKey ? getRegisteredStackSurface(previousSurfaceKey) : null,
    [previousSurfaceKey],
  );

  const stopUnderlayAnimation = useCallback(() => {
    underlayAnimationRef.current?.stop();
    underlayAnimationRef.current = null;
  }, []);

  const stopSettleAnimation = useCallback(() => {
    settleAnimationRef.current?.stop();
    settleAnimationRef.current = null;
  }, []);

  const stopSurfaceAnimation = useCallback(() => {
    surfaceAnimationRef.current?.stop();
    surfaceAnimationRef.current = null;
  }, []);

  const stopOpacityAnimation = useCallback(() => {
    opacityAnimationRef.current?.stop();
    opacityAnimationRef.current = null;
  }, []);

  const stopEnterAnimationFrame = useCallback(() => {
    if (enterAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(enterAnimationFrameRef.current);
      enterAnimationFrameRef.current = null;
    }
  }, []);

  const stopEnsureVisibleFrame = useCallback(() => {
    if (ensureVisibleFrameRef.current !== null) {
      window.cancelAnimationFrame(ensureVisibleFrameRef.current);
      ensureVisibleFrameRef.current = null;
    }
  }, []);

  const finishClose = useCallback(
    (value?: any) => {
      logStackNavigator("surface.finishClose", {
        key: entry.key,
        title: entry.title,
        value: value ?? closeValueRef.current,
      });
      onCloseComplete(entry.key, value ?? closeValueRef.current);
    },
    [entry.key, entry.title, onCloseComplete],
  );

  const closeSurface = useCallback(
    (value?: any) => {
      if (isClosingRef.current) {
        logStackNavigator("surface.close.skipped", {
          key: entry.key,
          title: entry.title,
          value,
        });
        return;
      }

      logStackNavigator("surface.close", {
        key: entry.key,
        title: entry.title,
        value,
        renderedAsIOS,
      });
      isClosingRef.current = true;
      closeValueRef.current = value;
      isSwipingRef.current = false;
      isInteractiveIOSRef.current = false;
      setIsSwiping(false);
      stopEnterAnimationFrame();
      stopEnsureVisibleFrame();
      stopSettleAnimation();
      stopSurfaceAnimation();
      stopOpacityAnimation();
      stopUnderlayAnimation();

      const animationsToWait: PromiseLike<unknown>[] = [];

      surfaceAnimationRef.current = animate(
        renderedAsIOS ? x : y,
        renderedAsIOS ? getViewportWidth() + 32 : getViewportHeight() + 32,
        transition,
      );
      animationsToWait.push(
        new Promise<void>((resolve) => {
          surfaceAnimationRef.current?.then(() => resolve());
        }),
      );

      if (!renderedAsIOS) {
        opacityAnimationRef.current = animate(opacity, 0, transition);
        animationsToWait.push(
          new Promise<void>((resolve) => {
            opacityAnimationRef.current?.then(() => resolve());
          }),
        );
      }

      if (renderedAsIOS) {
        underlayProgress.set(getIOSUnderlayProgress(x.get()));
        underlayAnimationRef.current = animate(underlayProgress, 0, transition);
        animationsToWait.push(
          new Promise<void>((resolve) => {
            underlayAnimationRef.current?.then(() => resolve());
          }),
        );
      }

      Promise.all(animationsToWait).then(() => {
        finishClose();
      });
    },
    [
      finishClose,
      opacity,
      renderedAsIOS,
      stopEnterAnimationFrame,
      stopEnsureVisibleFrame,
      stopOpacityAnimation,
      stopSurfaceAnimation,
      stopSettleAnimation,
      stopUnderlayAnimation,
      transition,
      underlayProgress,
      x,
      y,
      entry.key,
      entry.title,
    ],
  );

  useLayoutEffect(() => {
    logStackNavigator("surface.register", {
      key: entry.key,
      title: entry.title,
      hasNode: !!surfaceHostRef.current,
    });
    registeredStackSurfaces.set(entry.key, surfaceHostRef.current);
    return () => {
      logStackNavigator("surface.unregister", {
        key: entry.key,
        title: entry.title,
      });
      registeredStackSurfaces.delete(entry.key);
    };
  }, [entry.key, entry.title]);

  useLayoutEffect(() => {
    if (!renderedAsIOS || !surfaceHostRef.current) {
      return;
    }

    underlayTargetsRef.current = resolveUnderlayTargets(getUnderlaySurface());
    logStackNavigator("surface.underlayTargets", {
      key: entry.key,
      title: entry.title,
      previousSurfaceKey,
      targets: underlayTargetsRef.current.map((target) => target.kind),
    });
    prepareUnderlayTargets(underlayTargetsRef.current);
    applyUnderlayProgress(underlayTargetsRef.current, 0);

    return () => {
      stopUnderlayAnimation();
      stopSettleAnimation();
      stopSurfaceAnimation();
      stopOpacityAnimation();
      stopEnterAnimationFrame();
      stopEnsureVisibleFrame();
      restoreUnderlayTargets(underlayTargetsRef.current);
      underlayTargetsRef.current = [];
    };
  }, [
    entry.key,
    entry.title,
    getUnderlaySurface,
    previousSurfaceKey,
    renderedAsIOS,
    stopEnterAnimationFrame,
    stopEnsureVisibleFrame,
    stopOpacityAnimation,
    stopSurfaceAnimation,
    stopSettleAnimation,
    stopUnderlayAnimation,
  ]);

  useEffect(() => {
    if (!renderedAsIOS) {
      return;
    }

    return underlayProgress.on("change", (latest) => {
      applyUnderlayProgress(underlayTargetsRef.current, latest);
    });
  }, [renderedAsIOS, underlayProgress]);

  useEffect(() => {
    if (!renderedAsIOS) {
      return;
    }

    const handleResize = () => {
      applyUnderlayProgress(underlayTargetsRef.current, underlayProgress.get());
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [renderedAsIOS, underlayProgress]);

  useEffect(() => {
    if (!renderedAsIOS) {
      return;
    }

    return x.on("change", (latest) => {
      if (!isInteractiveIOSRef.current) {
        return;
      }

      underlayProgress.set(getIOSUnderlayProgress(latest));
    });
  }, [renderedAsIOS, underlayProgress, x]);

  const { ref: swipeableRef, ...swipeHandlers } = useSwipeable({
    onSwipeStart: (eventData) => {
      if (!isTopScreen || isClosingRef.current) {
        return;
      }

      const validDirection = renderedAsIOS
        ? eventData.dir === "Right" && eventData.initial[0] <= IOS_SWIPE_ZONE
        : eventData.dir === "Down";

      const atTopAndClosed =
        renderedAsIOS ||
        (parseInt(y.get().toString(), 10) === 0 &&
          bodyRef.current?.scrollTop === 0);

      if (!validDirection || !atTopAndClosed) {
        return;
      }

      logStackNavigator("surface.swipeStart", {
        key: entry.key,
        title: entry.title,
        dir: eventData.dir,
      });

      prevDelta.current = { x: 0, y: 0 };
      lastDirRef.current = null;
      stopUnderlayAnimation();
      stopSettleAnimation();
      stopSurfaceAnimation();
      stopOpacityAnimation();
      x.set(0);
      y.set(0);
      opacity.set(1);

      if (renderedAsIOS) {
        isInteractiveIOSRef.current = true;
        underlayProgress.set(1);
      }

      isSwipingRef.current = true;
      setIsSwiping(true);
    },
    onSwiping: ({ deltaX, deltaY, event }) => {
      if (!isSwipingRef.current || isClosingRef.current) {
        return;
      }

      const dx = deltaX - prevDelta.current.x;
      const dy = deltaY - prevDelta.current.y;
      prevDelta.current = { x: deltaX, y: deltaY };

      if (renderedAsIOS) {
        if (Math.abs(dx) > 0) {
          lastDirRef.current = dx > 0 ? "Right" : "Left";
          if (deltaX > 0) {
            if ("cancelable" in event && event.cancelable) {
              event.preventDefault();
            }
            x.set(deltaX);
            underlayProgress.set(getIOSUnderlayProgress(deltaX));
          }
        }
      } else if (Math.abs(dy) > 0) {
        lastDirRef.current = dy > 0 ? "Down" : "Up";
        if (deltaY > 0) {
          if ("cancelable" in event && event.cancelable) {
            event.preventDefault();
          }
          y.set(deltaY);
        }
      }
    },
    onSwiped: () => {
      if (!isSwipingRef.current || isClosingRef.current) {
        return;
      }

      isSwipingRef.current = false;

      if (renderedAsIOS) {
        if (lastDirRef.current === "Right") {
          isClosingRef.current = true;
          const animation = animate(
            x,
            getViewportWidth() + 32,
            IOS_CLOSE_SWIPE_TRANSITION,
          );
          settleAnimationRef.current = animation;
          animation.then(() => {
            isInteractiveIOSRef.current = false;
            finishClose();
          });
        } else {
          const animation = animate(x, 0, IOS_REBOUND_SWIPE_TRANSITION);
          settleAnimationRef.current = animation;
          animation.then(() => {
            isInteractiveIOSRef.current = false;
            underlayProgress.set(1);
            setIsSwiping(false);
          });
        }
      } else if (lastDirRef.current === "Down") {
        isClosingRef.current = true;
        const animation = animate(
          y,
          getViewportHeight() + 32,
          ANDROID_CLOSE_SWIPE_TRANSITION,
        );
        settleAnimationRef.current = animation;
        animation.then(() => {
          finishClose();
        });
      } else {
        const animation = animate(y, 0, ANDROID_REBOUND_SWIPE_TRANSITION);
        settleAnimationRef.current = animation;
        animation.then(() => {
          setIsSwiping(false);
        });
      }
    },
    trackTouch: true,
    trackMouse: false,
    preventScrollOnSwipe: false,
    touchEventOptions: { passive: false },
  });

  useLayoutEffect(() => {
    const unlockScroll = lockStackedScroll(isNativeMobileApp);
    let cancelled = false;
    logStackNavigator("surface.enter.prepare", {
      key: entry.key,
      title: entry.title,
      renderedAsIOS,
      shouldDeferBodyMount,
    });

    if (shouldDeferBodyMount) {
      setIsBodyReady(false);
    } else {
      setIsBodyReady(true);
    }

    stopSurfaceAnimation();
    stopOpacityAnimation();
    x.set(renderedAsIOS ? getViewportWidth() : 0);
    y.set(renderedAsIOS ? 0 : getViewportHeight());
    opacity.set(renderedAsIOS ? 1 : 0);

    if (renderedAsIOS) {
      underlayProgress.set(0);
      stopUnderlayAnimation();
    }

    enterAnimationFrameRef.current = window.requestAnimationFrame(() => {
      enterAnimationFrameRef.current = null;

      if (cancelled || isClosingRef.current) {
        logStackNavigator("surface.enter.cancelled", {
          key: entry.key,
          title: entry.title,
          cancelled,
          isClosing: isClosingRef.current,
        });
        return;
      }

      logStackNavigator("surface.enter.start", {
        key: entry.key,
        title: entry.title,
        rect: surfaceMotionRef.current?.getBoundingClientRect(),
      });
      if (renderedAsIOS) {
        underlayAnimationRef.current = animate(underlayProgress, 1, transition);
      }

      surfaceAnimationRef.current = animate(
        renderedAsIOS ? x : y,
        0,
        transition,
      );
      if (!renderedAsIOS) {
        opacityAnimationRef.current = animate(opacity, 1, transition);
      }

      const animationsToWait: PromiseLike<unknown>[] = [
        new Promise<void>((resolve) => {
          surfaceAnimationRef.current?.then(() => resolve());
        }),
      ];

      if (!renderedAsIOS) {
        animationsToWait.push(
          new Promise<void>((resolve) => {
            opacityAnimationRef.current?.then(() => resolve());
          }),
        );
      }

      Promise.all(animationsToWait).then(() => {
        if (!cancelled && !isClosingRef.current) {
          if (surfaceMotionRef.current) {
            const rect = surfaceMotionRef.current.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(
              surfaceMotionRef.current,
            );
            logStackNavigator("surface.enter.finalCheck", {
              key: entry.key,
              title: entry.title,
              left: rect.left,
              right: rect.right,
              width: rect.width,
              transform: computedStyle.transform,
              opacity: computedStyle.opacity,
            });

            if (isSurfaceOffscreen(rect)) {
              logStackNavigator("surface.enter.forceVisible", {
                key: entry.key,
                title: entry.title,
              });
              x.set(0);
              y.set(0);
              opacity.set(1);
              surfaceMotionRef.current.style.transform =
                "translate3d(0px, 0px, 0px)";
              surfaceMotionRef.current.style.webkitTransform =
                "translate3d(0px, 0px, 0px)";
              surfaceMotionRef.current.style.opacity = "1";
            }
          }

          logStackNavigator("surface.enter.end", {
            key: entry.key,
            title: entry.title,
          });
          setIsBodyReady(true);
        }
      });

      ensureVisibleFrameRef.current = window.requestAnimationFrame(() => {
        ensureVisibleFrameRef.current = null;

        if (cancelled || isClosingRef.current || !surfaceMotionRef.current) {
          return;
        }

        const rect = surfaceMotionRef.current.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(surfaceMotionRef.current);
        logStackNavigator("surface.ensureVisible.check", {
          key: entry.key,
          title: entry.title,
          left: rect.left,
          right: rect.right,
          width: rect.width,
          transform: computedStyle.transform,
        });
        if (rect.left >= window.innerWidth - 1 || rect.right <= 1) {
          logStackNavigator("surface.ensureVisible.forceEnter", {
            key: entry.key,
            title: entry.title,
          });
          x.set(0);
          y.set(0);
          opacity.set(1);
          if (renderedAsIOS) {
            underlayProgress.set(1);
          }
          surfaceMotionRef.current.style.transform =
            "translate3d(0px, 0px, 0px)";
          surfaceMotionRef.current.style.webkitTransform =
            "translate3d(0px, 0px, 0px)";
          surfaceMotionRef.current.style.opacity = "1";
          setIsBodyReady(true);
        }
      });
    });

    return () => {
      cancelled = true;
      logStackNavigator("surface.enter.cleanup", {
        key: entry.key,
        title: entry.title,
      });
      stopEnterAnimationFrame();
      stopEnsureVisibleFrame();
      unlockScroll();
    };
  }, [
    entry.key,
    entry.title,
    isNativeMobileApp,
    opacity,
    renderedAsIOS,
    shouldDeferBodyMount,
    stopEnterAnimationFrame,
    stopEnsureVisibleFrame,
    stopOpacityAnimation,
    stopSurfaceAnimation,
    stopUnderlayAnimation,
    transition,
    underlayProgress,
    x,
    y,
  ]);

  useEffect(() => {
    if (!entry.requestedClose || isClosingRef.current) {
      return;
    }

    logStackNavigator("surface.requestedClose", {
      key: entry.key,
      title: entry.title,
      value: entry.closeValue,
    });
    closeSurface(entry.closeValue);
  }, [closeSurface, entry.closeValue, entry.requestedClose]);

  const surfaceStyle = (isNativeMobileApp
    ? {
        [IOS_STACKED_UNDERLAY_OFFSET_VAR]: "0px",
        transform: `translateX(var(${IOS_STACKED_UNDERLAY_OFFSET_VAR}, 0px))`,
        height: "var(--be-viewport-height, 100vh)",
        minHeight: "100dvh",
        zIndex:
          STACK_SURFACE_Z_INDEX_BASE + stackIndex * STACK_SURFACE_Z_INDEX_STEP,
        touchAction: renderedAsIOS && isTopScreen ? "pan-y" : undefined,
        pointerEvents: isTopScreen ? "auto" : "none",
      }
    : {
        [IOS_STACKED_UNDERLAY_OFFSET_VAR]: "0px",
        transform: `translateX(var(${IOS_STACKED_UNDERLAY_OFFSET_VAR}, 0px))`,
        height: "var(--be-viewport-height, 100vh)",
        minHeight: "100dvh",
        zIndex:
          STACK_SURFACE_Z_INDEX_BASE + stackIndex * STACK_SURFACE_Z_INDEX_STEP,
        pointerEvents: isTopScreen ? "auto" : "none",
      }) as unknown as CSSProperties;

  const motionSurfaceStyle = {
    ...(renderedAsIOS ? { x } : { y, opacity }),
    width: "100%",
    height: "100%",
    minHeight: "100%",
    paddingTop: isNativeMobileApp ? "var(--safe-top)" : undefined,
    paddingRight: isNativeMobileApp ? "var(--safe-right)" : undefined,
    paddingBottom: isNativeMobileApp ? "var(--safe-bottom)" : undefined,
    paddingLeft: isNativeMobileApp ? "var(--safe-left)" : undefined,
  } as const;

  const dialogProps = useMemo(
    () => ({
      id: entry.key,
      "aria-labelledby": `${entry.key}-label`,
      "aria-describedby": `${entry.key}-description`,
    }),
    [entry.key],
  );

  const dialogContextValue = useMemo(
    () => ({
      labelId: `${entry.key}-label`,
      descriptionId: `${entry.key}-description`,
      type: "stacked" as const,
      isDismissable: entry.isDismissable,
      close: (value?: any) => closeSurface(value),
      value: undefined,
      setValue: () => undefined,
      initialValue: undefined,
      formId: `${entry.key}-form`,
      dialogProps,
      disableInitialTransition: false,
    }),
    [closeSurface, dialogProps, entry.isDismissable, entry.key],
  );

  const stackScreenContextValue = useMemo(
    () => ({
      key: entry.key,
      canGoBack,
      isTopScreen,
      close: (value?: any) => closeSurface(value),
      setOptions: (options: Partial<StackScreenOptions>) => {
        stackNavigatorState.update(entry.key, options);
      },
    }),
    [canGoBack, closeSurface, entry.key, isTopScreen],
  );

  const renderedContent =
    typeof entry.content === "function"
      ? entry.content(dialogContextValue)
      : entry.content;
  const contentWithPlayerStore = entry.playerStore ? (
    <PlayerStoreContext.Provider value={entry.playerStore}>
      {renderedContent}
    </PlayerStoreContext.Provider>
  ) : (
    renderedContent
  );
  const contentWithPreservedRouterContext =
    entry.routeContext && entry.locationContext ? (
      <UNSAFE_RouteContext.Provider value={entry.routeContext as any}>
        <UNSAFE_LocationContext.Provider value={entry.locationContext as any}>
          {contentWithPlayerStore}
        </UNSAFE_LocationContext.Provider>
      </UNSAFE_RouteContext.Provider>
    ) : (
      contentWithPlayerStore
    );

  return (
    <div
      ref={surfaceHostRef}
      className={clsx(
        "stack fixed flex flex-col",
        entry.className,
        isNativeMobileApp && "bg-transparent",
      )}
      role="dialog"
      aria-modal="true"
      aria-hidden={!isTopScreen}
      style={
        {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          ...surfaceStyle,
        } as any
      }
    >
      <m.div
        ref={(node) => {
          surfaceMotionRef.current = node;
          swipeableRef(node);
        }}
        className={clsx(
          "flex h-full w-full flex-col",
          isNativeMobileApp &&
            "player-ambient-scene native-overlay-motion-surface",
        )}
        style={motionSurfaceStyle as any}
        {...(isTopScreen ? swipeHandlers : {})}
      >
        <div className="stack-header">
          <button onClick={() => closeSurface()}>
            {renderedAsIOS ? <ChevronLeftIcon /> : <ArrowBackIcon />}
            <span className="ml-3 mt-3">
              <Trans message={entry.title || "Go back"} />
            </span>
          </button>
          {entry.stackedHeaderRight}
        </div>
        <div
          ref={bodyRef}
          className={clsx(
            "stack-body",
            entry.stackedBodyPadding !== false ? "p-16" : "",
            isSwiping ? "overflow-y-hidden" : "",
            !isBodyReady && "pointer-events-none",
          )}
          aria-busy={!isBodyReady}
        >
          {isBodyReady ? (
            <StackScreenContext.Provider value={stackScreenContextValue}>
              <DialogContext.Provider value={dialogContextValue as any}>
                {contentWithPreservedRouterContext}
              </DialogContext.Provider>
            </StackScreenContext.Provider>
          ) : null}
        </div>
      </m.div>
    </div>
  );
}

export function StackOutlet() {
  const entries = useStackNavigatorStore((state) => state.entries);
  const closeTop = useStackNavigatorStore((state) => state.closeTop);

  useEffect(() => {
    logStackNavigator("outlet.entries", {
      entries: entries.map((entry) => entry.key),
      count: entries.length,
    });
  }, [entries]);

  useEffect(() => {
    if (!entries.length) {
      return;
    }

    let backButtonListenerId: AppEventId;

    const setupBackButtonListener = async () => {
      backButtonListenerId = await useAppEventManager.addListener(
        "backButton",
        () => {
          closeTop();
        },
        { exclusive: true, priority: 100, platform: "android" },
      );
    };

    setupBackButtonListener();

    return () => {
      useAppEventManager.removeListenerById(backButtonListenerId);
    };
  }, [closeTop, entries.length]);

  return (
    <>
      {entries.map((entry, stackIndex) => {
        if (!entry.renderInOutlet) {
          return null;
        }

        return (
          <StackSurface
            key={entry.key}
            entry={entry}
            stackIndex={stackIndex}
            isTopScreen={stackIndex === entries.length - 1}
            canGoBack={stackIndex > 0}
            previousSurfaceKey={
              stackIndex > 0 ? entries[stackIndex - 1].key : null
            }
            onCloseComplete={stackNavigatorState.completeClose}
          />
        );
      })}
    </>
  );
}
